import { useState, useEffect, useMemo } from "react";
import { useAdminLanguage } from "../../layouts/AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Video, Clock, Trash2, ArrowUpDown, ChevronUp, ChevronDown, Play, Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { exportToCSV } from "../../utils/exportCSV";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminInterviews() {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("started_at");
  const [sortDir, setSortDir] = useState("desc");
  const [videoDialog, setVideoDialog] = useState(null);
  const { t } = useAdminLanguage();

  useEffect(() => { fetchInterviews(); }, []);

  const fetchInterviews = async () => {
    try {
      const res = await fetch(`${API}/api/admin/interviews`, { credentials: 'include' });
      if (res.ok) setInterviews(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    return sortDir === "asc" ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />;
  };

  const sorted = useMemo(() => {
    return [...interviews].sort((a, b) => {
      let va = a[sortKey] ?? "", vb = b[sortKey] ?? "";
      if (typeof va === "number") return sortDir === "asc" ? va - vb : vb - va;
      return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
  }, [interviews, sortKey, sortDir]);

  const handleDelete = async (sessionId) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      const res = await fetch(`${API}/api/admin/interviews/${sessionId}`, { method: "DELETE", credentials: "include" });
      if (res.ok) { toast.success(t("interview_deleted")); fetchInterviews(); }
    } catch (e) { toast.error(t("connection_error")); }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "-";
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return `${m}m ${s}s`;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1">{t("interviews")}</h1>
          <p className="text-zinc-500 text-sm">Donnees des 60 derniers jours uniquement (auto-nettoyage).</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="border-zinc-700 text-zinc-400 hover:text-white" data-testid="export-interviews-csv" onClick={() => exportToCSV(sorted, [
            { label: "Session ID", key: "session_id" },
            { label: "Recruteur", accessor: r => r.recruiter?.name || "N/A" },
            { label: "Entreprise", accessor: r => r.recruiter?.company_name || "" },
            { label: "Candidat", key: "candidate_name" },
            { label: "Score", key: "overall_score" },
            { label: "Duree (sec)", key: "duration_seconds" },
            { label: "Video", accessor: r => r.has_video ? "Oui" : "Non" },
            { label: "Date", key: "started_at" }
          ], "ema_entretiens")}>
            <Download className="w-4 h-4 mr-2" />CSV
          </Button>
          <Badge className="bg-zinc-800 text-zinc-400">{interviews.length} entretiens</Badge>
        </div>
      </div>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800">
                <TableHead className="text-zinc-400 cursor-pointer select-none" onClick={() => handleSort("session_id")}>
                  <span className="flex items-center">{t("session_id")}<SortIcon col="session_id" /></span>
                </TableHead>
                <TableHead className="text-zinc-400 cursor-pointer select-none" onClick={() => handleSort("recruiter")}>
                  <span className="flex items-center">{t("recruiter_name")}<SortIcon col="recruiter" /></span>
                </TableHead>
                <TableHead className="text-zinc-400 cursor-pointer select-none" onClick={() => handleSort("candidate_name")}>
                  <span className="flex items-center">{t("candidate_name")}<SortIcon col="candidate_name" /></span>
                </TableHead>
                <TableHead className="text-zinc-400 cursor-pointer select-none" onClick={() => handleSort("overall_score")}>
                  <span className="flex items-center">{t("score")}<SortIcon col="overall_score" /></span>
                </TableHead>
                <TableHead className="text-zinc-400 cursor-pointer select-none" onClick={() => handleSort("duration_seconds")}>
                  <span className="flex items-center">{t("real_duration")}<SortIcon col="duration_seconds" /></span>
                </TableHead>
                <TableHead className="text-zinc-400">{t("video")}</TableHead>
                <TableHead className="text-zinc-400 cursor-pointer select-none" onClick={() => handleSort("started_at")}>
                  <span className="flex items-center">{t("date")}<SortIcon col="started_at" /></span>
                </TableHead>
                <TableHead className="text-right text-zinc-400">{t("actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map(row => (
                <TableRow key={row.session_id} className="border-zinc-800 hover:bg-zinc-800/50">
                  <TableCell className="font-mono text-zinc-500 text-xs">{row.session_id?.slice(0, 12)}...</TableCell>
                  <TableCell>
                    <div>
                      <span className="text-white text-sm">{row.recruiter?.name || "N/A"}</span>
                      <p className="text-zinc-500 text-xs">{row.recruiter?.company_name || ""}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-zinc-300 text-sm">{row.candidate_name || "Inconnu"}</span>
                    {row.candidate_email && <p className="text-zinc-500 text-xs">{row.candidate_email}</p>}
                  </TableCell>
                  <TableCell>
                    {row.overall_score ? (
                      <span className={`font-bold text-sm ${row.overall_score > 70 ? "text-emerald-400" : row.overall_score > 50 ? "text-yellow-400" : "text-red-400"}`}>
                        {row.overall_score}%
                      </span>
                    ) : <span className="text-zinc-600">-</span>}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 text-zinc-400 text-xs">
                      <Clock className="w-3 h-3" />
                      {formatDuration(row.duration_seconds)}
                    </div>
                  </TableCell>
                  <TableCell>
                    {row.has_video ? (
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-indigo-400 hover:text-indigo-300" onClick={() => setVideoDialog(row.session_id)}>
                        <Play className="w-4 h-4" />
                      </Button>
                    ) : <span className="text-zinc-700 text-xs">{t("no_video")}</span>}
                  </TableCell>
                  <TableCell className="text-zinc-500 text-xs">{row.started_at ? new Date(row.started_at).toLocaleString() : "-"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-400 hover:text-red-400" onClick={() => handleDelete(row.session_id)} title={t("delete")}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {sorted.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-zinc-500 py-8">{t("no_data")}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Video Dialog */}
      <Dialog open={!!videoDialog} onOpenChange={() => setVideoDialog(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white sm:max-w-2xl">
          <DialogHeader><DialogTitle>Video Entretien</DialogTitle></DialogHeader>
          {videoDialog && (
            <video controls className="w-full rounded-lg mt-2" src={`${API}/api/interview/v2/video/${videoDialog}`}>
              Votre navigateur ne supporte pas la lecture video.
            </video>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
